// Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../css/Sidebar.css';

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState(() => {
    const stored = localStorage.getItem('sidebarExpandedMenus');
    return stored ? JSON.parse(stored) : {};
  });
  const [vinculo] = useState(() => {
    const storedVinculo = localStorage.getItem('vinculo');
    return storedVinculo ? JSON.parse(storedVinculo) : null;
  });
  const [user] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const isActive = path => {
    return location.pathname === path;
  };

  const toggleSubmenu = index => {
    // Se o sidebar estiver fechado, abre ele primeiro
    if (!isOpen) {
      toggleSidebar();
    }
    setExpandedMenus(prev => {
      const isOpening = !prev[index];
      let updated = {};
      if (isOpening) {
        // abrir apenas o submenu clicado, fechando os demais
        updated = { [index]: true };
      } else {
        // fechar todos
        updated = {};
      }
      localStorage.setItem('sidebarExpandedMenus', JSON.stringify(updated));
      return updated;
    });
  };

  const isOrgaoUser = user?.tipoUsuario === 'ORGAO';

  const menuItems = [
    {
      title: 'Home',
      icon: 'bi-house-door',
      path: '/home',
      show: true,
    },
        {
          title: 'Demandas',
          icon: 'bi-clipboard-check',
          path: '/demandas',
          show: true,
        },
        {
          title: 'Órgãos',
          icon: 'bi-building',
          path: '/orgaos',
          show: !isOrgaoUser && vinculo?.tipo === 'ADMIN',
        },
    {
      title: 'Administração',
      icon: 'bi-gear',
      show: !isOrgaoUser && vinculo?.tipo === 'ADMIN' && user?.role === 'ADMIN',
      submenu: [
        {
          title: 'Entidades',
          icon: 'bi-building',
          path: '/entidades',
        },
        {
          title: 'Usuários',
          icon: 'bi-people',
          path: '/usuarios',
        },
        {
          title: 'Sessões Ativas',
          icon: 'bi-list-task',
          path: '/sessions-ativas',
        },
        {
          title: 'Imagens',
          icon: 'bi-image',
          path: '/imagens',
        },
        {
          title: 'Margens',
          icon: 'bi-rulers',
          path: '/margens',
        },
      ],
    },
  ];

  // Fecha submenus que não contenham a rota atual. Mantém aberto somente o submenu cujo subitem corresponde à rota.
  useEffect(() => {
    let keepIndex = null;
    menuItems.forEach((item, idx) => {
      if (item.submenu) {
        const match = item.submenu.some(si => si.path && location.pathname.startsWith(si.path));
        if (match) keepIndex = idx;
      }
    });

    if (keepIndex !== null) {
      const updated = { [keepIndex]: true };
      setExpandedMenus(prev => {
        if (JSON.stringify(prev) === JSON.stringify(updated)) return prev;
        localStorage.setItem('sidebarExpandedMenus', JSON.stringify(updated));
        return updated;
      });
    } else {
      setExpandedMenus(prev => {
        if (!prev || Object.keys(prev).length === 0) return prev;
        localStorage.setItem('sidebarExpandedMenus', JSON.stringify({}));
        return {};
      });
    }
  }, [location.pathname]);

  const handleNavigation = path => {
    navigate(path);
    if (window.innerWidth < 992) {
      toggleSidebar();
    }
  };

  return (
    <>
      {/* Overlay para mobile */}
      {isOpen && <div className="sidebar-overlay" onClick={toggleSidebar} />}

      {/* Sidebar */}
      <aside className={`modern-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header d-none d-lg-flex">
          <button
            className={`sidebar-link ${!isOpen ? '' : 'justify-content-end pe-2'}`}
            onClick={toggleSidebar}
            aria-label={isOpen ? 'Recolher menu' : 'Expandir menu'}
            title={isOpen ? 'Recolher menu' : 'Expandir menu'}
          >
            <div className="sidebar-link-content">
              <i className={`bi bi-${isOpen ? 'x-lg' : 'list'}`}></i>
            </div>
            {isOpen && <span className="ms-auto"></span>}
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {menuItems.map((item, index) => {
              if (!item.show) return null;

              // Destacar menus quando estiver em suas páginas
              const adminPaths = [
                '/entidade',
                '/entidades',
                '/usuario',
                '/usuarios',
                '/vinculos',
                '/sessions-ativas',
                '/imagens',
                '/margens',
              ];
              
              const isAdminMenu =
                item.title === 'Administração' &&
                adminPaths.some(p => location.pathname.startsWith(p));
              
              const isMenuActive = isAdminMenu;

              if (item.submenu) {
                return (
                  <li
                    key={index}
                    className={`sidebar-item has-submenu${
                      isMenuActive ? ' active' : ''
                    }`}
                  >
                    <button
                      className={`sidebar-link ${
                        expandedMenus[index] ? 'expanded' : ''
                      }${isMenuActive ? ' active' : ''}`}
                      onClick={() => toggleSubmenu(index)}
                      title={!isOpen ? item.title : ''}
                    >
                      <div className="sidebar-link-content">
                        <i className={`bi ${item.icon}`}></i>
                      </div>
                      {isOpen && (
                        <>
                          <span>{item.title}</span>
                          <i
                            className={`bi bi-chevron-${
                              expandedMenus[index] ? 'down' : 'right'
                            } ms-auto submenu-arrow`}
                          ></i>
                        </>
                      )}
                    </button>
                    {isOpen && expandedMenus[index] && (
                      <ul className="sidebar-submenu">
                        {item.submenu.map((subitem, subindex) => (
                          <li key={subindex}>
                            <button
                              className={`sidebar-sublink ${
                                isActive(subitem.path) ? 'active' : ''
                              }`}
                              onClick={() => handleNavigation(subitem.path)}
                              title={subitem.title}
                            >
                              <i className={`bi ${subitem.icon}`}></i>
                              <span>{subitem.title}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              return (
                <li
                  key={index}
                  className={`sidebar-item${
                    isActive(item.path) ? ' active' : ''
                  }`}
                >
                  <button
                    className={`sidebar-link${
                      isActive(item.path) ? ' active' : ''
                    }`}
                    onClick={() => handleNavigation(item.path)}
                    title={!isOpen ? item.title : ''}
                  >
                    <div className="sidebar-link-content">
                      <i className={`bi ${item.icon}`}></i>
                    </div>
                    {isOpen && <span>{item.title}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
